/* eslint-disable max-lines */
import { Button, Spinner } from '@heroui/react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useHeroToast } from '../../hooks';
import { ContentLoadError } from '../ContentLoadError';
import { Flex } from '../Flex';

const GOOGLE_MAPS_API_KEY = `${import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

interface MarkerPositionProps {
  lat: number;
  lng: number;
}

interface GeoPointProps {
  latitude: number;
  longitude: number;
}

interface AddressProps {
  city_name?: string;
  city: string;
  street?: string;
  number?: string;
  postal_code?: string;
  geoPoint: GeoPointProps;
}

interface GoogleMapComponentProps {
  isDraggable?: boolean;
  setCoordinate?: (markerPosition: MarkerPositionProps) => void;
  address?: AddressProps;
  showMap?: boolean;
  mapHeight?: string;
}

interface GeocodeResponse {
  status: string;
  results: Array<{
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  }>;
}

// Funkcja walidacji współrzędnych
const isValidLatitude = (lat: number | undefined | null): lat is number => typeof lat === 'number' && !Number.isNaN(lat) && lat >= -90 && lat <= 90;

const isValidLongitude = (lng: number | undefined | null): lng is number => typeof lng === 'number' && !Number.isNaN(lng) && lng >= -180 && lng <= 180;

export const GoogleMapComponent: FC<GoogleMapComponentProps> = ({
  isDraggable = false,
  setCoordinate,
  address,
  showMap = false,
  mapHeight = '400px',
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const { showError } = useHeroToast();
  const [mapVisible, setMapVisible] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isFetchingCoordinates, setIsFetchingCoordinates] = useState<boolean>(false);

  // Ref do śledzenia czy już pokazano błąd walidacji
  const hasShownValidationError = useRef<boolean>(false);

  const initialPosition = useMemo(
    (): MarkerPositionProps => {
      const lat = address?.geoPoint?.latitude;
      const lng = address?.geoPoint?.longitude;

      // Walidacja współrzędnych
      const validLat = isValidLatitude(lat);
      const validLng = isValidLongitude(lng);

      return {
        lat: validLat ? lat : 54.352025,
        lng: validLng ? lng : 18.646639,
      };
    },
    [address?.geoPoint?.latitude, address?.geoPoint?.longitude],
  );

  const [markerPosition, setMarkerPosition] = useState<MarkerPositionProps>(initialPosition);

  const containerStyle = useMemo(
    () => ({
      width: '100%',
      height: mapHeight,
      borderRadius: '8px',
    }),
    [mapHeight],
  );

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    mapInstance.setZoom(16);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const onMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent): void => {
      if (event.latLng) {
        const newPosition: MarkerPositionProps = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        setMarkerPosition(newPosition);
        setCoordinate?.(newPosition);
      }
    },
    [setCoordinate],
  );

  const fetchCoordinates = useCallback(async (): Promise<void> => {
    if (!address?.city?.trim()) {
      showError('Nazwa miasta jest wymagana');
      return;
    }

    setIsFetchingCoordinates(true);

    const addressString = [
      address.street,
      address.number,
      address.postal_code,
      address.city,
    ]
      .filter(Boolean)
      .join(', ');

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          addressString,
        )}&key=${GOOGLE_MAPS_API_KEY}`,
      );

      if (!response.ok) {
        throw new Error('Błąd odpowiedzi serwera');
      }

      const data: GeocodeResponse = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const { location } = data.results[0].geometry;
        const newPosition: MarkerPositionProps = {
          lat: location.lat,
          lng: location.lng,
        };

        setMarkerPosition(newPosition);
        setCoordinate?.(newPosition);
        setMapVisible(true);
        hasShownValidationError.current = false; // Reset flagi błędu
      } else if (data.status === 'ZERO_RESULTS') {
        showError('Nie znaleziono adresu. Sprawdź poprawność danych.');
      } else if (data.status === 'REQUEST_DENIED') {
        console.error('API Key error:', data);
        showError('Błąd autoryzacji API. Sprawdź klucz Google Maps API.');
      } else if (data.status === 'INVALID_REQUEST') {
        showError('Nieprawidłowe żądanie. Sprawdź format adresu.');
      } else {
        showError(`Błąd geokodowania: ${data.status}`);
      }
    } catch (error) {
      console.error('Error fetching coordinates:', error);
      showError('Błąd podczas pobierania lokalizacji. Spróbuj ponownie.');
    } finally {
      setIsFetchingCoordinates(false);
    }
  }, [address, setCoordinate, showError]);

  // Aktualizuj pozycję tylko gdy zmienią się współrzędne z adresu i showMap jest true
  useEffect(() => {
    if (showMap && address?.geoPoint?.latitude !== undefined && address?.geoPoint?.longitude !== undefined) {
      const lat = address.geoPoint.latitude;
      const lng = address.geoPoint.longitude;

      // Walidacja przed ustawieniem pozycji
      const validLat = isValidLatitude(lat);
      const validLng = isValidLongitude(lng);

      if (validLat && validLng) {
        const newPosition: MarkerPositionProps = {
          lat,
          lng,
        };
        setMarkerPosition(newPosition);
        setMapVisible(true);
        hasShownValidationError.current = false; // Reset flagi błędu
      } else if (!hasShownValidationError.current) {
        console.warn('Nieprawidłowe współrzędne geograficzne:', { lat, lng });
        if (!validLat) {
          showError('Nieprawidłowa szerokość geograficzna (zakres: -90 do 90)');
        }
        if (!validLng) {
          showError('Nieprawidłowa długość geograficzna (zakres: -180 do 180)');
        }
        hasShownValidationError.current = true;
      }
    }
  }, [address?.geoPoint?.latitude, address?.geoPoint?.longitude, showMap]); // Usunięto showError z dependencies

  // Wyśrodkuj mapę na nowej pozycji markera
  useEffect(() => {
    if (map && markerPosition.lat !== 0 && markerPosition.lng !== 0) {
      map.panTo(markerPosition);
    }
  }, [map, markerPosition]);

  if (loadError) {
    return <ContentLoadError message="Błąd ładowania Google Maps." />;
  }

  if (!isLoaded) {
    return (
      <Flex justifyContent="center" itemAlign="center" margin="20px 0">
        <Spinner size="lg" label="Ładowanie mapy..." />
      </Flex>
    );
  }

  const hasValidAddress = Boolean(address?.city);
  const addressDisplay = [
    address?.city_name || address?.city,
    address?.street,
    address?.number,
    address?.postal_code,
  ]
    .filter(Boolean)
    .join(' ');

  // Sprawdzenie czy współrzędne są prawidłowe do wyświetlenia mapy
  const hasValidCoordinates = isValidLatitude(markerPosition.lat)
      && isValidLongitude(markerPosition.lng)
      && markerPosition.lat !== 0
      && markerPosition.lng !== 0;

  return (
    <div className="w-full">
      {hasValidAddress && (

        <Button
          className="w-full mb-2.5"
          isLoading={isFetchingCoordinates}
          isDisabled={!address?.city?.trim()}
          radius="lg"
          onPress={fetchCoordinates}
          color="secondary"
          startContent={!isFetchingCoordinates && <MapPin size={16} />}
          variant="solid"
          size="lg"
          aria-label="Generuj współrzędne na podstawie adresu"
        >
          <strong className="whitespace-nowrap">
            Generuj współrzędne dla adresu:
            {addressDisplay && (
            <span className="text-md font-normal">
              {' '}
              {addressDisplay}
            </span>
            )}
          </strong>
        </Button>

      )}

      {mapVisible && hasValidCoordinates && (
        <div className="shadow-lg rounded-lg overflow-hidden">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={markerPosition}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              streetViewControl: true,
              mapTypeControl: true,
              fullscreenControl: true,
              zoomControl: true,
            }}
          >
            <Marker
              draggable={isDraggable}
              position={markerPosition}
              onDragEnd={onMarkerDragEnd}
              title={addressDisplay || 'Lokalizacja'}
            />
          </GoogleMap>
        </div>
      )}
    </div>
  );
};
